import { useState } from 'react';
import { useAuth } from './AuthContext';
import { FaUser, FaEnvelope, FaCamera, FaSave, FaTimes, FaTrash, FaKey } from 'react-icons/fa';
import './UserProfile.css';

function UserProfile({ onClose, onUpdateProfile }) {
  const { user, updateProfile, uploadAvatar, deleteAvatar, updatePassword } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar || '');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please select an image file' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image size should be less than 5MB' });
      return;
    }

    setUploading(true);
    setMessage({ type: '', text: '' });

    const result = await uploadAvatar(file);
    
    if (result.success) {
      setAvatarPreview(result.url);
      setMessage({ type: 'success', text: 'Avatar uploaded successfully!' });
      if (onUpdateProfile) {
        onUpdateProfile({ avatar: result.url });
      }
    } else {
      setMessage({ type: 'error', text: result.error || 'Upload failed' });
    }
    
    setUploading(false);
  };

  const handleRemoveAvatar = async () => {
    if (!window.confirm('Are you sure you want to remove your avatar?')) return;
    
    setUploading(true);
    const result = await deleteAvatar();
    
    if (result.success) {
      setAvatarPreview(null);
      setMessage({ type: 'success', text: 'Avatar removed successfully!' });
      if (onUpdateProfile) {
        onUpdateProfile({ avatar: null });
      }
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to remove avatar' });
    }
    
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    const result = await updateProfile({ name, email });
    
    if (result.success) {
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      if (onUpdateProfile) {
        onUpdateProfile({ name, email });
      }
      setTimeout(() => onClose(), 1500);
    } else {
      setMessage({ type: 'error', text: result.error || 'Update failed' });
    }
    
    setLoading(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }
    
    setLoading(true);
    const result = await updatePassword(currentPassword, newPassword);
    if (result.success) {
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setShowPasswordForm(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to change password' });
    }
    setLoading(false);
  };

  return (
    <div className="profile-modal-overlay" onClick={onClose}>
      <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
        <div className="profile-modal-header">
          <h2><FaUser /> Profile Settings</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="profile-form">
          {message.text && (
            <div className={`profile-message ${message.type}`}>
              {message.text}
            </div>
          )}

          <div className="avatar-section">
            <div className="avatar-preview">
              {avatarPreview ? (
                <img src={avatarPreview} alt="Avatar" />
              ) : (
                <div className="avatar-placeholder">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
              )}
              <div className="avatar-actions">
                <label className="avatar-upload-label">
                  <FaCamera />
                  <input type="file" accept="image/*" onChange={handleAvatarChange} hidden disabled={uploading} />
                </label>
                {avatarPreview && (
                  <button type="button" className="avatar-remove-label" onClick={handleRemoveAvatar} disabled={uploading}>
                    <FaTrash />
                  </button>
                )}
              </div>
            </div>
            <p className="upload-hint">Click camera to upload (Max 5MB)</p>
          </div>

          <div className="profile-field">
            <FaUser className="field-icon" />
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full Name"
              required
            />
          </div>

          <div className="profile-field">
            <FaEnvelope className="field-icon" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email Address"
              required
            />
          </div>

          <div className="profile-role">
            <strong>Role:</strong> 
            <span className={`role-badge ${user?.role}`}>
              {user?.role?.replace('_', ' ').toUpperCase()}
            </span>
          </div>

          {!showPasswordForm ? (
            <button type="button" className="change-password-btn" onClick={() => setShowPasswordForm(true)}>
              <FaKey /> Change Password
            </button>
          ) : (
            <div className="password-form">
              <h4>Change Password</h4>
              <div className="profile-field">
                <input
                  type="password"
                  placeholder="Current Password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
              </div>
              <div className="profile-field">
                <input
                  type="password"
                  placeholder="New Password (min 6 characters)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
              <div className="profile-field">
                <input
                  type="password"
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <div className="password-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowPasswordForm(false)}>
                  Cancel
                </button>
                <button type="button" className="save-btn" onClick={handleChangePassword}>
                  Update Password
                </button>
              </div>
            </div>
          )}

          <div className="profile-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              <FaTimes /> Cancel
            </button>
            <button type="submit" className="save-btn" disabled={loading || uploading}>
              <FaSave /> {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UserProfile;